import * as React from "react";
import { CalendarCheck, CalendarX2 } from "lucide-react";

import { amendPollBallot, apiErrorMessage, getPollBallot } from "@/api/client";
import { PollBallot as PollBallotView, PollVoteChoice } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Card, HeaderCard, PublicFooter } from "../public/PublicChrome";
import { PollBallot } from "./PollBallot";
import "../PublicRollingLinkPage.css";
import { PublicLoadingLoop, useLoadingWindow } from "../public/PublicLoading";

/**
 * A respondent changing their own answers, at {@code /poll/manage/{token}}. The unguessable token
 * from their receipt is the whole of the authorisation — the same model the booking and rolling link
 * manage pages use.
 *
 * <p>The same ballot as the voting page, arriving pre-filled. Counts show here when the host allows
 * them, which by definition is after this person has voted, since holding a token means they have.</p>
 */

const WHEN = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  day: "numeric",
  month: "long",
  hour: "2-digit",
  minute: "2-digit",
});

type Load =
  | { status: "loading" }
  | { status: "error"; kind: "not-found" | "unreachable" }
  | { status: "ready"; ballot: PollBallotView };

export function PublicPollManagePage({ token }: { token: string }) {
  const [load, setLoad] = React.useState<Load>({ status: "loading" });
  const [answers, setAnswers] = React.useState<Record<string, PollVoteChoice>>({});
  const [name, setName] = React.useState("");
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchBallot = React.useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const ballot = await getPollBallot(token);
      setLoad({ status: "ready", ballot });
      setName(ballot.name);
      setNote(ballot.note ?? "");
      setAnswers(
        Object.fromEntries(
          ballot.options.filter((o) => o.my_choice).map((o) => [o.id, o.my_choice as PollVoteChoice]),
        ),
      );
    } catch (e) {
      const notFound =
        typeof e === "object" && e !== null && "status" in e && (e as { status: number }).status === 404;
      setLoad({ status: "error", kind: notFound ? "not-found" : "unreachable" });
    }
  }, [token]);

  React.useEffect(() => {
    void fetchBallot();
  }, [fetchBallot]);

  const ballot = load.status === "ready" ? load.ballot : null;

  const submit = async () => {
    if (!ballot) return;
    setSaving(true);
    setError(null);
    try {
      const next = await amendPollBallot(token, {
        name,
        email: ballot.email,
        note: note.trim() || null,
        votes: Object.entries(answers).map(([option_id, choice]) => ({ option_id, choice })),
      });
      setLoad({ status: "ready", ballot: next });
      setSaved(true);
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't update your answers"));
    } finally {
      setSaving(false);
    }
  };

  // Held on screen for one full revolution once it appears, and not painted at all
  // if the fetch beats 120ms. The ready and error branches gate on it too: during
  // the hold the fetch has finished, so otherwise both would render at once.
  const showLoading = useLoadingWindow(load.status === "loading");

  return (
    <div className="inv-theme min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto flex max-w-2xl flex-col gap-5">
        {showLoading && (
          <Card>
            <PublicLoadingLoop />
          </Card>
        )}

        {!showLoading && load.status === "error" && (
          <Card className="p-8 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
              <CalendarX2 className="h-6 w-6 text-amber-700 dark:text-amber-300" />
            </div>
            {load.kind === "not-found" ? (
              <>
                <h1 className="text-lg font-bold text-foreground">This link doesn't work</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Check the link in your email, or ask the organiser to send it again.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-lg font-bold text-foreground">We couldn't reach the server</h1>
                <p className="mt-1 text-sm text-muted-foreground">Give it a moment and try again.</p>
                <Button className="mt-4" onClick={() => void fetchBallot()}>
                  Try again
                </Button>
              </>
            )}
          </Card>
        )}

        {ballot && (
          <>
            <HeaderCard
              name={ballot.host_name}
              subtitle={ballot.title}
              avatarUrl={ballot.host_avatar_url}
              bannerUrl={ballot.host_banner_url}
              socialLinks={ballot.host_social_links}
            />

            {ballot.status === "CONFIRMED" ? (
              <Card className="space-y-3 p-6 text-center">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal/20">
                  <CalendarCheck className="h-6 w-6 text-brand-teal" />
                </div>
                <h1 className="text-lg font-bold text-foreground">The time is settled</h1>
                {ballot.options
                  .filter((o) => o.id === ballot.confirmed_option_id)
                  .map((o) => (
                    <p key={o.id} className="text-sm font-medium text-foreground">
                      {WHEN.format(new Date(o.start_at))}
                    </p>
                  ))}
                {ballot.meeting_url && (
                  <a
                    href={ballot.meeting_url}
                    className="inline-block text-sm font-medium text-brand-blue underline-offset-2 hover:underline"
                  >
                    Join link
                  </a>
                )}
              </Card>
            ) : (
              <Card className="space-y-4 p-6">
                <h2 className="text-sm font-bold text-foreground">
                  {ballot.voting_open ? "Your answers" : "Voting has closed"}
                </h2>

                {ballot.voting_open && (
                  <div className="space-y-1">
                    <Label htmlFor="poll-name">Your name</Label>
                    <Input
                      id="poll-name"
                      value={name}
                      maxLength={120}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                )}

                <PollBallot
                  rows={ballot.options}
                  answers={answers}
                  onAnswer={(id, choice) => {
                    setSaved(false);
                    setAnswers((prev) => ({ ...prev, [id]: choice }));
                  }}
                  showCounts={ballot.counts_visible}
                  readOnly={!ballot.voting_open}
                />

                {ballot.voting_open && (
                  <>
                    <div className="space-y-1">
                      <Label htmlFor="poll-note">Anything to add? (optional)</Label>
                      <textarea
                        id="poll-note"
                        value={note}
                        maxLength={500}
                        rows={2}
                        onChange={(e) => setNote(e.target.value)}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                      />
                    </div>

                    {error && <p className="text-sm font-medium text-brand-coral">{error}</p>}
                    {saved && !error && (
                      <p className="text-sm font-medium text-brand-teal">Your answers are updated.</p>
                    )}

                    <Button
                      onClick={() => void submit()}
                      disabled={saving || Object.keys(answers).length === 0}
                      className="w-full"
                    >
                      {saving ? "Saving…" : "Update my vote"}
                    </Button>
                  </>
                )}

                {!ballot.voting_open && ballot.respond_by && (
                  <p className="text-sm text-foreground">
                    Answers closed on {ballot.respond_by}, {ballot.timezone} time.
                  </p>
                )}
              </Card>
            )}
          </>
        )}

        <PublicFooter />
      </div>
    </div>
  );
}
